import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { AppRole, AppUser } from '../models/user.model';
import { OAUTH_CONFIG } from './oauth-config';

interface TokenResponse {
  access_token?: string;
  accessToken?: string;
  token?: string;
  id_token?: string;
  token_type?: string;
  expires_in?: number;
  expiresIn?: number;
  refresh_token?: string;
  refreshToken?: string;
  scope?: string;
}

const LS_ACCESS = 'pd_access_token';
const LS_REFRESH = 'pd_refresh_token';
const LS_EXPIRES = 'pd_expires_at';
const LS_USER = 'pd_user';
const SS_STATE = 'pd_oauth_state';
const SS_RETURN = 'pd_post_login_redirect';

function randomState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Гибкий маппинг ответа userinfo провайдера в AppUser. */
function mapToAppUser(raw: Record<string, unknown>): AppUser {
  const pick = (...keys: string[]): unknown => {
    for (const k of keys) {
      const v = raw[k];
      if (v !== undefined && v !== null && v !== '') return v;
    }
    return undefined;
  };
  const id = String(pick('sub', 'id', 'uid', 'userId', 'login', 'username', 'email') ?? 'oauth-user');
  const direct = pick('fullName', 'displayName', 'name', 'fio', 'cn');
  const firstName = pick('firstName', 'first_name', 'given_name');
  const lastName = pick('lastName', 'last_name', 'family_name');
  const fioParts = [firstName, lastName].filter(Boolean).join(' ');
  const login = pick('login', 'username');
  const fullName = String(direct ?? (fioParts !== '' ? fioParts : (login ?? id)));
  const email = String(pick('email', 'mail', 'e-mail') ?? '');
  const rawRoles = pick('roles', 'role', 'groups', 'authorities', 'orgs', 'permissions');
  const roles = normalizeRoles(rawRoles);
  return { id, fullName, email, roles };
}

function normalizeRoles(raw: unknown): AppRole[] {
  const known: AppRole[] = ['OPERATOR', 'ACCOUNTANT', 'ADMIN'];
  const list: string[] = Array.isArray(raw)
    ? raw.map((r) => String(typeof r === 'object' && r !== null ? (r as Record<string, unknown>)['name'] ?? (r as Record<string, unknown>)['role'] ?? r : r))
    : typeof raw === 'string'
      ? raw.split(/[,\s]+/)
      : [];
  const upper = list.map((r) => r.toUpperCase());
  const mapped = upper
    .map((r): AppRole | null => {
      if ((known as string[]).includes(r)) return r as AppRole;
      // Частые синонимы провайдера: кассир/оператор, бухгалтер, админ.
      if (['CASHIER', 'OPERATOR', 'КАССИР', 'ОПЕРАТОР'].includes(r)) return 'OPERATOR';
      if (['ACCOUNTANT', 'BUH', 'БУХ', 'БУХГАЛТЕР'].includes(r)) return 'ACCOUNTANT';
      if (['ADMIN', 'ADMINISTRATOR', 'АДМИН', 'АДМИНИСТРАТОР'].includes(r)) return 'ADMIN';
      return null;
    })
    .filter((r): r is AppRole => r !== null);
  // По умолчанию — оператор, чтобы guard'ы работали предсказуемо.
  return [...new Set(mapped)].length ? [...new Set(mapped)] : ['OPERATOR'];
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly config = inject(OAUTH_CONFIG);

  private readonly _user = signal<AppUser | null>(loadUser());
  private readonly _accessToken = signal<string | null>(load(LS_ACCESS));
  private readonly _pending = signal(false);
  private readonly _error = signal<string | null>(null);

  readonly user = this._user.asReadonly();
  readonly isLoading = this._pending.asReadonly();
  readonly lastError = this._error.asReadonly();
  readonly isAuthenticated = computed(() => !!this._accessToken() && !!this._user());

  /** Шаг 1 Authorization Code flow: редирект браузера на oauth/<systemId>/authorize. */
  login(returnUrl?: string): void {
    this._error.set(null);
    try {
      const state = randomState();
      sessionStorage.setItem(SS_STATE, state);
      sessionStorage.setItem(SS_RETURN, returnUrl ?? this.router.url ?? '/payments');
      const url =
        `${this.config.authorizeUrl}?` +
        new HttpParams({
          fromObject: {
            response_type: 'code',
            client_id: this.config.clientId,
            redirect_uri: this.config.redirectUri,
            scope: this.config.scope,
            state,
          },
        }).toString();
      this.document.location.assign(url);
    } catch {
      this._error.set('Не удалось начать вход через OAuth2.');
    }
  }

  /**
   * Шаг 2: обмен code на токены в oauth/<systemId>/access_token, затем загрузка oauth/<systemId>/userinfo.
   * Вызывается из AuthCallbackComponent.
   */
  handleCallback(code: string, state: string | null): Observable<AppUser> {
    const expected = sessionStorage.getItem(SS_STATE);
    if (!state || !expected || state !== expected) {
      return throwError(() => new Error('Недействительный state. Попробуйте войти ещё раз.'));
    }
    sessionStorage.removeItem(SS_STATE);
    this._pending.set(true);
    this._error.set(null);

    const body = new HttpParams({
      fromObject: {
        grant_type: 'authorization_code',
        code,
        redirect_uri: this.config.redirectUri,
        client_id: this.config.clientId,
      },
    }).toString();

    return this.http
      .post<TokenResponse>(this.config.tokenUrl, body, {
        headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      })
      .pipe(
        switchMap((token) => {
          const access = token.access_token ?? token.accessToken ?? token.token;
          if (!access) throw new Error('Провайдер не вернул access_token.');
          const refresh = token.refresh_token ?? token.refreshToken ?? null;
          const expiresIn = Number(token.expires_in ?? token.expiresIn ?? 3600);
          this.storeTokens(access, refresh, expiresIn);
          return this.loadProfile();
        }),
        tap({
          next: () => this._pending.set(false),
          error: (err) => {
            this._pending.set(false);
            this._error.set(friendlyError(err));
            this.clearSession();
          },
        }),
        catchError((err) => throwError(() => new Error(friendlyError(err)))),
      );
  }

  /** Профиль текущего пользователя по access_token. */
  loadProfile(): Observable<AppUser> {
    const token = this._accessToken();
    if (!token) return throwError(() => new Error('Нет access_token.'));
    return this.http.get<Record<string, unknown>>(this.config.userinfoUrl).pipe(
      map(mapToAppUser),
      tap((user) => {
        this._user.set(user);
        try {
          localStorage.setItem(LS_USER, JSON.stringify(user));
        } catch {
          // ignore storage errors
        }
      }),
    );
  }

  /** Попытка восстановить сессию после перезагрузки страницы. */
  restoreSession(): Observable<AppUser | null> {
    if (!this._accessToken() || !this._user()) return of(null);
    if (this.isExpired()) {
      this.clearSession();
      return of(null);
    }
    // Обновляем профиль в фоне, но не разлогиниваем при сетевой ошибке.
    return this.loadProfile().pipe(catchError(() => of(this._user())));
  }

  logout(): void {
    this.clearSession();
    this.router.navigateByUrl('/');
    // По желанию: завершить SSO-сессию и на стороне провайдера.
    // if (this.config.logoutUrl) this.document.location.assign(this.config.logoutUrl);
  }

  getAccessToken(): string | null {
    return this.isExpired() ? null : this._accessToken();
  }

  hasRole(role: string): boolean {
    return this._user()?.roles.includes(role as AppRole) ?? false;
  }

  consumeReturnUrl(fallback = '/payments'): string {
    const url = sessionStorage.getItem(SS_RETURN) ?? fallback;
    sessionStorage.removeItem(SS_RETURN);
    return url.startsWith('/') ? url : fallback;
  }

  private storeTokens(access: string, refresh: string | null, expiresInSec: number): void {
    this._accessToken.set(access);
    try {
      localStorage.setItem(LS_ACCESS, access);
      if (refresh) localStorage.setItem(LS_REFRESH, refresh);
      else localStorage.removeItem(LS_REFRESH);
      localStorage.setItem(LS_EXPIRES, String(Date.now() + Math.max(expiresInSec - 30, 0) * 1000));
    } catch {
      // ignore storage errors
    }
  }

  private isExpired(): boolean {
    try {
      const raw = localStorage.getItem(LS_EXPIRES);
      return !!raw && Date.now() > Number(raw);
    } catch {
      return false;
    }
  }

  private clearSession(): void {
    this._user.set(null);
    this._accessToken.set(null);
    try {
      localStorage.removeItem(LS_ACCESS);
      localStorage.removeItem(LS_REFRESH);
      localStorage.removeItem(LS_EXPIRES);
      localStorage.removeItem(LS_USER);
    } catch {
      // ignore storage errors
    }
  }
}

function load(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function loadUser(): AppUser | null {
  try {
    const raw = localStorage.getItem(LS_USER);
    if (!raw) return null;
    const user = JSON.parse(raw) as AppUser;
    return user?.id ? user : null;
  } catch {
    return null;
  }
}

function friendlyError(err: unknown): string {
  if (err instanceof Error && !isHttpError(err)) return err.message;
  const status = (err as { status?: number })?.status;
  if (status === 0) return 'OAuth-сервер недоступен. Проверьте сеть и OAUTH_ISSUER в .env.';
  if (status === 400) return 'Провайдер отклонил запрос (400). Проверьте client_id и redirect_uri.';
  if (status === 401 || status === 403) return 'Не удалось обменять код на токен (401/403).';
  if (status === 404) return 'Endpoint не найден (404). Проверьте пути oauth/pay/* в .env.';
  return 'Ошибка входа через OAuth2. Попробуйте ещё раз.';
}

function isHttpError(err: unknown): boolean {
  return typeof err === 'object' && err !== null && 'status' in err;
}
